import { writeFn } from '@/events/layerSystem'
import { rasterizeSphere } from '@/shapes'
import { parseKeyXYZ, posKeyXYZ, type Position } from '@/voxelMath'
import type { VoxelData, VoxelMap } from '@/voxelTypes'
import { canonicalVoxelType } from '@/voxels/registry'

import type { Spine } from './spineCore'

const LUMP_DIRECTIONS: readonly Position[] = [
  { x: 1, y: 0, z: 0 },
  { x: -1, y: 0, z: 0 },
  { x: 0, y: 1, z: 0 },
  { x: 0, y: -1, z: 0 },
  { x: 0, y: 0, z: 1 },
  { x: 0, y: 0, z: -1 },
  { x: 1, y: 1, z: 0 },
  { x: -1, y: 1, z: 0 },
  { x: 1, y: -1, z: 0 },
  { x: -1, y: -1, z: 0 },
  { x: 1, y: 0, z: 1 },
  { x: -1, y: 0, z: 1 },
  { x: 1, y: 0, z: -1 },
  { x: -1, y: 0, z: -1 },
  { x: 0, y: 1, z: 1 },
  { x: 0, y: -1, z: 1 },
  { x: 0, y: 1, z: -1 },
  { x: 0, y: -1, z: -1 },
]

function isSameVoxelType(a: VoxelData | undefined, b: VoxelData | undefined): boolean {
  return !!a && !!b && canonicalVoxelType(a.type) === canonicalVoxelType(b.type)
}

function canWriteSameTypeAt(target: VoxelMap, key: number, voxel: VoxelData): boolean {
  const existing = target.get(key)
  return !existing || isSameVoxelType(existing, voxel)
}

function hasSameTypeNeighbor(target: VoxelMap, center: Position, voxel: VoxelData, bounds: Position): boolean {
  for (let dx = -1; dx <= 1; dx++) {
    for (let dy = -1; dy <= 1; dy++) {
      for (let dz = -1; dz <= 1; dz++) {
        if ((dx * dx + dy * dy + dz * dz) > 1) continue
        const nx = center.x + dx
        const ny = center.y + dy
        const nz = center.z + dz
        if (nx < 0 || nx >= bounds.x || ny < 0 || ny >= bounds.y || nz < 0 || nz >= bounds.z) continue
        const key = posKeyXYZ(nx, ny, nz)
        if (isSameVoxelType(target.get(key), voxel)) return true
      }
    }
  }
  return false
}

function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0
  return Math.max(0, Math.min(1, value))
}

function hash32(value: number): number {
  let x = value >>> 0
  x ^= x >>> 16
  x = Math.imul(x, 0x7feb352d)
  x ^= x >>> 15
  x = Math.imul(x, 0x846ca68b)
  x ^= x >>> 16
  return x >>> 0
}

function hashUnit(key: number, salt: number): number {
  return hash32((key >>> 0) ^ Math.imul(salt, 0x9e3779b1)) / 0xffffffff
}

function writeSameTypeSphere(
  target: VoxelMap,
  write: ReturnType<typeof writeFn>,
  center: Position,
  radius: number,
  voxel: VoxelData,
  bounds: Position,
): void {
  rasterizeSphere(center, Math.max(0.5, radius), voxel, bounds, (key, nextVoxel) => {
    if (!canWriteSameTypeAt(target, key, nextVoxel)) return
    write(key, nextVoxel)
  })
}

function writeLumpySameTypeSphere(
  target: VoxelMap,
  write: ReturnType<typeof writeFn>,
  center: Position,
  key: number,
  radius: number,
  lumpiness: number,
  voxel: VoxelData,
  bounds: Position,
): void {
  writeSameTypeSphere(target, write, center, radius, voxel, bounds)

  const lumpCount = Math.round(clamp01(lumpiness) * 3)
  const usedDirections = new Set<number>()
  for (let i = 0; i < lumpCount; i++) {
    let directionIndex = hash32(key + i * 131) % LUMP_DIRECTIONS.length
    while (usedDirections.has(directionIndex)) {
      directionIndex = (directionIndex + 1) % LUMP_DIRECTIONS.length
    }
    usedDirections.add(directionIndex)

    const direction = LUMP_DIRECTIONS[directionIndex]
    const offsetDistance = hashUnit(key, i + 17) > 0.82 && radius >= 1.25 ? 2 : 1
    const lumpRadius = radius * (0.74 + hashUnit(key, i + 31) * 0.12)
    writeSameTypeSphere(
      target,
      write,
      {
        x: center.x + direction.x * offsetDistance,
        y: center.y + direction.y * offsetDistance,
        z: center.z + direction.z * offsetDistance,
      },
      lumpRadius,
      voxel,
      bounds,
    )
  }
}

export function expandSameTypeScribble(
  spine: Spine,
  bounds: Position,
  target: VoxelMap,
  opts: { radius: number; seedRadius?: number; lumpiness?: number },
): void {
  const write = writeFn('place', target)
  const radius = Math.max(0.5, opts.radius)
  const seedRadius = Math.max(0.5, opts.seedRadius ?? 0.5)
  const lumpiness = clamp01(opts.lumpiness ?? 0)

  for (const point of spine) {
    const [x, y, z] = parseKeyXYZ(point.key)
    const center = { x, y, z }

    if (!canWriteSameTypeAt(target, point.key, point.voxel)) continue
    if (!hasSameTypeNeighbor(target, center, point.voxel, bounds)) {
      writeSameTypeSphere(target, write, center, seedRadius, point.voxel, bounds)
      continue
    }
    writeLumpySameTypeSphere(target, write, center, point.key, radius, lumpiness, point.voxel, bounds)
  }
}
